import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from 'react-query';

import dpiasApi from 'config/api/dpias/dpias';
import type { DpiaDecision } from 'config/api/dpias/dpias.types';
import MUTATION_KEYS from 'config/api/MUTATION_KEYS';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import { invalidateOwnerAssetRopaQueries } from 'pages/ProcessingActivities/_shared/invalidateOwnerAssetRopaQueries';
import {
  catchHttpStatuses,
  getUnavailableStatus,
  HTTP_UNAVAILABLE_STATUSES,
  isUnavailableQueryResult,
  unwrapAvailableQueryData,
} from 'pages/ProcessingActivities/_shared/queryUnavailable';
import useDpiaEditorContext from 'pages/ProcessingActivities/_shared/useDpiaEditorContext';

import type { OwnerDpiaFormValues } from '../OwnerDpiaEditorPage/ownerDpiaEditor.helpers';

import {
  mapDetailToReviewFormValues,
  validateApproveDecision,
  validateClarificationDecision,
  type AdminDpiaReviewValidationMessages,
} from './adminDpiaReview.helpers';

type Params = {
  dpiaId: number;
  onApproveSuccess?: () => void;
  onClarificationSuccess?: () => void;
  organizationId?: number;
};

const useAdminDpiaReview = ({ dpiaId, onApproveSuccess, onClarificationSuccess, organizationId }: Params) => {
  const queryClient = useQueryClient();
  const [decisionComment, setDecisionComment] = useState('');
  const [clarificationComment, setClarificationComment] = useState('');
  const [validationErrors, setValidationErrors] = useState<
    Partial<Record<'reviewComment' | 'approval' | 'decision' | 'clarificationComment', string>>
  >({});

  const detailQuery = useQuery(
    [QUERY_KEYS.DPIA_REVIEW_DETAIL, organizationId, dpiaId],
    async () =>
      catchHttpStatuses(async () => {
        const { data } = await dpiasApi.getReviewDetail(organizationId as number, dpiaId);
        return data;
      }, HTTP_UNAVAILABLE_STATUSES),
    {
      enabled: Boolean(organizationId && dpiaId),
      retry: false,
    },
  );

  const envelope = unwrapAvailableQueryData(detailQuery.data);
  const isUnavailable = isUnavailableQueryResult(detailQuery.data);
  const unavailableStatus = getUnavailableStatus(detailQuery.data);
  const { bannerMeta, context, isContextLoading, sourceTrigger } = useDpiaEditorContext({
    envelope,
    organizationId,
    useOrganizationAssetRopa: true,
  });

  const formValues = useMemo<OwnerDpiaFormValues | null>(() => {
    if (!envelope?.dpia) return null;
    return mapDetailToReviewFormValues(envelope.dpia);
  }, [envelope?.dpia]);

  useEffect(() => {
    if (envelope?.dpia) {
      setDecisionComment(envelope.dpia.reviewDecisionComment);
      setClarificationComment('');
      setValidationErrors({});
    }
  }, [envelope?.dpia]);

  const approveMutation = useMutation(
    MUTATION_KEYS.DPIA_APPROVE,
    async (decision: DpiaDecision) => {
      if (!envelope?.dpia) return null;

      const { data } = await dpiasApi.approve(organizationId as number, dpiaId, {
        decision,
        reviewDecisionComment: decisionComment,
      });

      return data;
    },
    {
      onSuccess: data => {
        if (!data) return;

        queryClient.setQueryData([QUERY_KEYS.DPIA_REVIEW_DETAIL, organizationId, dpiaId], data);
        invalidateOwnerAssetRopaQueries(queryClient, organizationId);
        onApproveSuccess?.();
      },
    },
  );

  const clarificationMutation = useMutation(
    MUTATION_KEYS.DPIA_REQUEST_CLARIFICATION,
    async () => {
      if (!envelope?.dpia) return null;

      const { data } = await dpiasApi.requestClarification(organizationId as number, dpiaId, {
        administratorClarificationComment: clarificationComment,
      });

      return data;
    },
    {
      onSuccess: data => {
        if (!data) return;

        queryClient.setQueryData([QUERY_KEYS.DPIA_REVIEW_DETAIL, organizationId, dpiaId], data);
        invalidateOwnerAssetRopaQueries(queryClient, organizationId);
        onClarificationSuccess?.();
      },
    },
  );

  const handleApprove = useCallback(
    async (decision: DpiaDecision, messages: AdminDpiaReviewValidationMessages) => {
      if (!envelope?.dpia || !formValues) return;

      const errors = validateApproveDecision(envelope.dpia, formValues, decision, decisionComment, messages);
      setValidationErrors(errors);

      if (Object.keys(errors).length > 0) {
        throw new Error('Validation failed');
      }

      await approveMutation.mutateAsync(decision);
    },
    [approveMutation, decisionComment, envelope?.dpia, formValues],
  );

  const handleRequestClarification = useCallback(
    async (messages: Pick<AdminDpiaReviewValidationMessages, 'clarificationComment'>) => {
      const errors = validateClarificationDecision(clarificationComment, messages);
      setValidationErrors(errors);

      if (Object.keys(errors).length > 0) {
        throw new Error('Validation failed');
      }

      await clarificationMutation.mutateAsync();
    },
    [clarificationComment, clarificationMutation],
  );

  return {
    approveMutation,
    clarificationMutation,
    bannerMeta,
    context,
    isContextLoading,
    sourceTrigger,
    detail: envelope?.dpia,
    formValues,
    handleApprove,
    handleRequestClarification,
    isError: detailQuery.isError,
    isLoading: detailQuery.isLoading,
    isUnavailable,
    unavailableStatus,
    isApproving: approveMutation.isLoading,
    isRequestingClarification: clarificationMutation.isLoading,
    refetch: detailQuery.refetch,
    decisionComment,
    setDecisionComment,
    clarificationComment,
    setClarificationComment,
    setValidationErrors,
    validationErrors,
  };
};

export default useAdminDpiaReview;
